import { createRouter, createWebHistory } from '@ionic/vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { activityLogRoutes } from '@/features/activity-log/routes';
import { adminRoutes } from '@/features/admin/routes';
import { analyticsRoutes } from '@/features/analytics/routes';
import { authRoutes } from '@/features/auth/routes';
import { catalogRoutes } from '@/features/catalog/routes';
import { billingPublicRoutes, centralRoutes } from '@/features/central/routes';
import { calculatorsRoutes } from '@/features/calculators/routes';
import { homeRoutes } from '@/features/home/routes';
import { customersRoutes } from '@/features/customers/routes';
import { vendorsRoutes } from '@/features/vendors/routes';
import { fixedAssetsRoutes } from '@/features/fixed-assets/routes';
import { dashboardRoutes } from '@/features/dashboard/routes';
import { employeesRoutes } from '@/features/employees/routes';
import { expensesRoutes } from '@/features/expenses/routes';
import { vehiclesRoutes } from '@/features/vehicles/routes';
import { dentalDashboardRoutes } from '@/features/dental-dashboard/routes';
import { dentalReportsRoutes } from '@/features/dental-reports/routes';
import { patientsRoutes } from '@/features/patients/routes';
import { treatmentPlansRoutes } from '@/features/treatment-plans/routes';
import { prescriptionsRoutes } from '@/features/prescriptions/routes';
import { dentalAppointmentsRoutes } from '@/features/dental-appointments/routes';
import { salonAppointmentsRoutes } from '@/features/salon-appointments/routes';
import { salonReportsRoutes } from '@/features/salon-reports/routes';
import { serviceOrdersRoutes } from '@/features/service-orders/routes';
import { appointmentsRoutes } from '@/features/appointments/routes';
import { industryRoutes } from '@/features/industry/routes';
import { invoicesRoutes } from '@/features/invoices/routes';
import { transactionsRoutes } from '@/features/transactions/routes';
import { notificationsRoutes } from '@/features/notifications/routes';
import { newsRoutes } from '@/features/news/routes';
import { payrollRoutes } from '@/features/payroll/routes';
import { portalRoutes } from '@/features/portal/routes';
import { posRoutes } from '@/features/pos/routes';
import { quotesRoutes } from '@/features/quotes/routes';
import { registryRoutes } from '@/features/registry/routes';
import { salesOrdersRoutes } from '@/features/sales-orders/routes';
import { reportsRoutes } from '@/features/reports/routes';
import { salesRoutes } from '@/features/sales/routes';
import { settingsRoutes } from '@/features/settings/routes';
import { timesheetsRoutes } from '@/features/timesheets/routes';
import {
  authGuard,
  centralAuthGuard,
  dentalScopeGuard,
  offlineGuard,
  permissionGuard,
  tenantGuard,
} from './guards';
import { loadPlatformMeta, platformName } from '@/core/platformMeta';

const placeholder = () => import('./placeholders/PlaceholderPage.vue');

function ph(title: string, description?: string) {
  return {
    component: placeholder,
    props: () => ({ title, description }),
  };
}

// Feature routes (real) + placeholders for modules that ship later.
const realRoutes: RouteRecordRaw[] = [
  ...dashboardRoutes,
  ...analyticsRoutes,
  ...posRoutes,
  ...salesRoutes,
  ...catalogRoutes,
  ...customersRoutes,
  ...vendorsRoutes,
  ...registryRoutes,
  ...quotesRoutes,
  ...industryRoutes,
  ...salesOrdersRoutes,
  ...invoicesRoutes,
  ...expensesRoutes,
  ...vehiclesRoutes,
  ...dentalDashboardRoutes,
  ...dentalReportsRoutes,
  ...patientsRoutes,
  ...treatmentPlansRoutes,
  ...prescriptionsRoutes,
  ...dentalAppointmentsRoutes,
  ...salonAppointmentsRoutes,
  ...salonReportsRoutes,
  ...serviceOrdersRoutes,
  ...appointmentsRoutes,
  ...transactionsRoutes,
  ...fixedAssetsRoutes,
  ...employeesRoutes,
  ...timesheetsRoutes,
  ...payrollRoutes,
  ...reportsRoutes,
  ...notificationsRoutes,
  ...newsRoutes,
  ...activityLogRoutes,
  ...settingsRoutes,
  ...adminRoutes,
];

const placeholderRoutes: RouteRecordRaw[] = [
  {
    path: 'profile',
    name: 'profile',
    ...ph('Profile'),
    meta: { title: 'Profile', requiresAuth: true },
  },
];

// Pages that render usefully from offline mirrors (products/stock/customers/sales
// are mirrored; dashboard is the safe offline landing). Everything else is
// online-only and blocked while offline by `offlineGuard`. Flagged centrally so
// feature route files stay untouched.
const OFFLINE_READY_ROUTES = new Set<string>([
  'dashboard',
  'pos',
  'sales',
  'sales.show',
  'customers',
  'customers.show',
  'customers.create',
  'catalog.products',
  'catalog.products.edit',
  'catalog.stock',
]);

function markOfflineReady(records: RouteRecordRaw[]): void {
  for (const r of records) {
    if (typeof r.name === 'string' && OFFLINE_READY_ROUTES.has(r.name)) {
      r.meta = { ...(r.meta ?? {}), offlineReady: true };
    }
    if (r.children) markOfflineReady(r.children);
  }
}
markOfflineReady(realRoutes);

const appChildren: RouteRecordRaw[] = [
  // `/` is claimed by the public homepage below; the app's own index only
  // resolves on tenant subdomains (the guard sends them here).
  { path: 'app', redirect: '/dashboard' },
  ...realRoutes,
  ...placeholderRoutes,
];

const routes: RouteRecordRaw[] = [
  ...homeRoutes,
  ...calculatorsRoutes,
  ...centralRoutes,
  ...billingPublicRoutes,
  ...portalRoutes,
  {
    path: '/auth',
    component: () => import('@/app/layouts/AuthLayout.vue'),
    children: [{ path: '', redirect: '/auth/login' }, ...authRoutes],
  },
  {
    path: '/',
    component: () => import('@/app/layouts/AppShell.vue'),
    children: appChildren,
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    ...ph('Not found', 'That page does not exist.'),
  },
];

export const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(tenantGuard);
router.beforeEach(centralAuthGuard);
router.beforeEach(authGuard);
router.beforeEach(permissionGuard);
router.beforeEach(dentalScopeGuard);
router.beforeEach(offlineGuard);

router.afterEach((to) => {
  if (to.meta.title) document.title = `${to.meta.title} · ${platformName.value}`;
});

// Console-configurable presentation (platform name, SEO tags, date/time
// formats) — fire-and-forget; defaults hold until it lands.
void loadPlatformMeta();
